"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Eye, EyeOff, PenSquare, Trash2, GripVertical, Plus, Search, X, Lock, ArrowUpToLine, ArrowDownToLine } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { EditFieldModal } from "@/components/edit-field-modal"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useSaveCurrentViewState } from "@/hooks/use-save-current-view-state"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { buildVisibleViewIds, serializeStageFilters, serializeGlobalFilters } from "@/lib/view-save-helpers"
import { getLocalizedField } from "@/utils/language-utils"
import { useLanguage } from '@/context/language-context'
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import { useFeatureFlags } from '@/contexts/feature-flag-context'

// Import AlertDialog components
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface EditColumnsProps {
  isOpen: boolean
  onClose: () => void
  mode?: 'edit' | 'create'
  viewTitle?: string
  onViewTitleChange?: (title: string) => void
  isPublic?: boolean
  onIsPublicChange?: (isPublic: boolean) => void
  isFromManageViews?: boolean
  viewId?: string | null
}

// Helper for sortable item
function SortableItem({ id, children, disabled = false }: { id: string, children: React.ReactNode, disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f3f4f6' : undefined,
        cursor: disabled ? 'not-allowed' : 'grab',
      }}
      {...attributes}
      {...(!disabled ? listeners : {})}
    >
      {children}
    </div>
  )
}

function ColumnActions({ column, onEdit, onDelete, canDeleteCustomColumns, canEditCustomColumns, canEditDefaultSettings, isVisible, onToggleVisibility }: {
  column: any,
  onEdit: () => void,
  onDelete: () => void,
  canDeleteCustomColumns: boolean,
  canEditCustomColumns: boolean,
  canEditDefaultSettings: boolean,
  isVisible?: boolean,
  onToggleVisibility?: () => void,
}) {
  // Per-row edit gate. Fields sourced from the shared Default partition (tenantName ==
  // "Default") are platform-level schema — editing one cascades to every tenant — so
  // they require the explicit EditDefaultSettings permission on top of the usual
  // EditColumns gate. Tenant-scoped fields keep the existing EditColumns-only behavior.
  // See the matching gate in field-edit-panel.tsx for the same fields surfaced from
  // the view designer.
  const isDefaultTenantField =
    typeof column?.tenantName === 'string' &&
    column.tenantName.toLowerCase() === 'default'
  const canEditThisColumn = canEditCustomColumns && (!isDefaultTenantField || canEditDefaultSettings)
  return (
    <div className="flex items-center gap-0.5">
      {onToggleVisibility && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleVisibility}
          className="p-1 h-auto w-auto rounded-md hover:bg-gray-100 transition-all"
          title={isVisible ? 'Hide column' : 'Show column'}
        >
          {isVisible ? <Eye size={15} className="text-gray-500 hover:text-gray-700" /> : <EyeOff size={15} className="text-gray-500 hover:text-gray-700" />}
        </Button>
      )}
      {canEditThisColumn && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          className="p-1 h-auto w-auto rounded-md hover:bg-blue-50 transition-all"
          title="Edit field"
        >
          <PenSquare size={15} className="text-gray-500 hover:text-blue-600" />
        </Button>
      )}
      {column.isCustomField && canDeleteCustomColumns && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          className="p-1 h-auto w-auto rounded-md hover:bg-red-50 transition-all"
          title="Delete field"
        >
          <Trash2 size={15} className="text-gray-500 hover:text-red-500" />
        </Button>
      )}
    </div>
  )
}

export function EditColumns({ isOpen, onClose, mode = 'edit', viewTitle = '', onViewTitleChange, isPublic = false, onIsPublicChange, isFromManageViews = false, viewId = null }: EditColumnsProps) {
  const { configuration, currentStage, deleteColumn, refreshConfiguration, currentView, stageFilters, globalFilters, state, setUnsavedChanges, updateVisibleColumns, viewsResponse } = useWorkflow()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { deleteFieldConfiguration, saveViewSettings, fetchView } = useWorkflowApi()
  const saveCurrentViewState = useSaveCurrentViewState()
  const { refreshViews } = useWorkflow()
  const { trackColumnAction, trackViewAction } = useWorkflowTracking()
  const [searchTerm, setSearchTerm] = useState("")
  const [isAddCustomColumnOpen, setIsAddCustomColumnOpen] = useState(false)
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null)
  const [editingColumn, setEditingColumn] = useState<any | null>(null)
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [stageFieldsOrder, setStageFieldsOrder] = useState<string[]>([])
  const [pendingVisibleFields, setPendingVisibleFields] = useState<string[]>([])
  const [isApplying, setIsApplying] = useState(false)
  // Snapshot of the managed view fetched on open. Used at save time so we don't
  // overwrite the managed view's other stages/filters with the live grid state.
  const [managedViewData, setManagedViewData] = useState<any | null>(null)
  const hasLoadedViewConfig = useRef(false)
  const { language } = useLanguage();
  const { isFeatureEnabled } = useFeatureFlags();

  // Feature flag to hide message column
  const isMessageColumnHidden = !isFeatureEnabled('messages');

  // Permission helpers
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
  const canDeleteCustomColumns = authorizedActions.includes("DeleteCustomColumns");
  const canEditCustomColumns = authorizedActions.includes("EditColumns");
  // Two-tier edit model: EditColumns covers tenant-scoped fields, EditDefaultSettings
  // covers the cross-tenant Default partition (mutating those changes column metadata
  // for every customer). Per-row decisioning lives in <ColumnActions>; we just thread
  // the permission down here. Reserved for platform admins / schema owners.
  const canEditDefaultSettings = authorizedActions.includes("EditDefaultSettings");
  const canHideColumnFromView = authorizedActions.includes("HideColumnFromView");

  // Find the current stage configuration
  const stageConfig = configuration?.stageConfigurations.find(
    (s) => s.stageCode.toLowerCase() === currentStage?.toLowerCase()
  )

  // Get all fields and current stage field IDs
  const stageFieldIds = stageConfig?.fieldConfigurations.map(f => f.fieldConfigurationId) || [];
  
  // Filter out Message column if feature flag is disabled (memoized to prevent infinite loops)
  const allFields = useMemo(() => {
    const allFieldsRaw = configuration?.fieldsInUse || [];
    return isMessageColumnHidden 
      ? allFieldsRaw.filter(f => f.columnCode !== 'Message')
      : allFieldsRaw;
  }, [configuration?.fieldsInUse, isMessageColumnHidden]);

  // V2: Helper to order fields with Message and Actions always first
  const orderFieldsWithFixedColumns = (fieldIds: string[]): string[] => {
    const messageFieldId = !isMessageColumnHidden ? allFields.find(f => f.columnCode === 'Message')?.fieldConfigurationId : undefined
    const actionsFieldId = 'actions-column' // V2: Actions column has fixed ID
    
    const otherFieldIds = fieldIds.filter(id => id !== messageFieldId && id !== actionsFieldId)
    const orderedIds: string[] = []
    
    if (messageFieldId && fieldIds.includes(messageFieldId)) {
      orderedIds.push(messageFieldId)
    }
    // V2: Actions column is always present but not in fieldsInUse, so we don't need to check if it's in fieldIds
    
    return [...orderedIds, ...otherFieldIds]
  };

  // Initialize pending changes when modal opens
  useEffect(() => {
    if (isOpen && stageConfig && !hasLoadedViewConfig.current) {
      hasLoadedViewConfig.current = true
      
      if (isFromManageViews && viewId) {
        // Load the specific view's configuration for the current stage
        const loadViewConfiguration = async () => {
          try {
            const viewData = await fetchView(viewId)
            setManagedViewData(viewData)

            // Find the configuration for the current stage
            const currentStageConfig = viewData.stageConfigurations?.find(
              (config: any) => config.stageConfigurationId === stageConfig.stageId
            )
            
            if (currentStageConfig) {
              // Get the field configuration IDs for this stage
              const visibleFieldIds = currentStageConfig.fieldConfigurationIds || []
              const orderedFieldIds = orderFieldsWithFixedColumns(visibleFieldIds)
              
              setPendingVisibleFields(visibleFieldIds)
              setStageFieldsOrder(orderedFieldIds)
            } else {
              // Fallback to current configuration if no stage config found
              const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
              
              setPendingVisibleFields([...stageFieldIds])
              setStageFieldsOrder(orderedFieldIds)
            }
          } catch (error: any) {
            console.error('Failed to load view configuration:', error)
            
            // If it's a 404, show error and close modal
            if (error?.response?.status === 404) {
              toast({
                variant: "destructive",
                title: t('error') || "Error",
                description: t('viewNotFound') || "View not found. It may have been deleted.",
              })
              
              // Refresh views and close modal
              try {
                await refreshViews()
              } catch (refreshError) {
                console.error('Failed to refresh views:', refreshError)
              }
              
              onClose()
              return
            }
            
            // For other errors, fallback to current configuration
            const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
            
            setPendingVisibleFields([...stageFieldIds])
            setStageFieldsOrder(orderedFieldIds)
          }
        }
        
        loadViewConfiguration()
      } else {
        // Use current stage configuration for regular edit columns or create new view
        const orderedFieldIds = orderFieldsWithFixedColumns(stageFieldIds)
        
        setPendingVisibleFields([...stageFieldIds])
        setStageFieldsOrder(orderedFieldIds)
      }
    }
  }, [isOpen, stageConfig, isFromManageViews, viewId, stageFieldIds])

  // Reset the ref when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasLoadedViewConfig.current = false
      setManagedViewData(null)
    }
  }, [isOpen])

  // Keep modal lists in sync if configuration updates while open
  useEffect(() => {
    if (!isOpen) return;
    const updatedStageConfig = configuration?.stageConfigurations.find(
      (s) => s.stageCode.toLowerCase() === currentStage?.toLowerCase()
    );
    if (!updatedStageConfig) return;
    const updatedVisibleIds = updatedStageConfig.fieldConfigurations.map(f => f.fieldConfigurationId);
    const orderedFieldIds = orderFieldsWithFixedColumns(updatedVisibleIds)
    
    setPendingVisibleFields([...updatedVisibleIds]);
    setStageFieldsOrder(orderedFieldIds);
  }, [isOpen, configuration?.stageConfigurations, currentStage, allFields, isMessageColumnHidden])

  // Shared search filter predicate
  const matchesSearch = (col: any) => {
    if (!searchTerm) return true
    const localizedName = getLocalizedField(col, 'columnName', language);
    return typeof localizedName === "string" &&
      localizedName.toLowerCase().includes(searchTerm.toLowerCase());
  }

  // Stage-specific fields (visible fields)
  const stageFields = (stageConfig?.fieldConfigurations || []).filter(matchesSearch);

  // Non-stage fields (hidden fields) - using the original logic
  const nonStageFields = allFields
    .filter(f => !pendingVisibleFields.includes(f.fieldConfigurationId))
    .filter(matchesSearch)
    .sort((a, b) => {
      const aName = getLocalizedField(a, 'columnName', language) || '';
      const bName = getLocalizedField(b, 'columnName', language) || '';
      return aName.localeCompare(bName);
    });

  // Visible fields filtered and sorted, with Message and History columns always first
  const visibleFields = allFields
    .filter(f => pendingVisibleFields.includes(f.fieldConfigurationId))
    .filter(matchesSearch)
    .sort((a, b) => {
      // Message column always comes first (if not hidden)
      if (!isMessageColumnHidden) {
        if (a.columnCode === 'Message') return -1;
        if (b.columnCode === 'Message') return 1;
      }

      // V2: Actions column is dynamically created, not part of fieldsInUse

      const aName = getLocalizedField(a, 'columnName', language) || '';
      const bName = getLocalizedField(b, 'columnName', language) || '';
      return aName.localeCompare(bName);
    });

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // DnD handler - only updates local state, not API
  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      // Find the field being dragged
      const draggedField = allFields.find(f => f.fieldConfigurationId === active.id)
      const targetField = allFields.find(f => f.fieldConfigurationId === over.id)
      
      // V2: Don't allow moving Message column (if not hidden)
      if (!isMessageColumnHidden && draggedField?.columnCode === 'Message') {
        return
      }
      
      // V2: Don't allow dropping on Message column (if not hidden)
      if (!isMessageColumnHidden && targetField?.columnCode === 'Message') {
        return
      }
      
      const oldIndex = stageFieldsOrder.indexOf(active.id)
      const newIndex = stageFieldsOrder.indexOf(over.id)
      const newOrder = arrayMove(stageFieldsOrder, oldIndex, newIndex)
      setStageFieldsOrder(newOrder)
    }
  }

  /**
   * The ids that actually RENDER, in order. `stageFieldsOrder` routinely holds ids with no row — the
   * list drops anything absent from the search-filtered `visibleFields`, and with the messages flag off
   * the Message id stays in the order while `allFields` drops the field. Anchoring the jump buttons on
   * the unfiltered order therefore produced visible no-ops: with a search active, "move to top" would
   * hop a row above a filtered-out entry, nothing moved on screen, and the saved order changed in a way
   * the user never saw. Both ends are anchored on this instead.
   */
  const renderedFieldIds = stageFieldsOrder.filter(id =>
    visibleFields.some(f => f.fieldConfigurationId === id))

  // Message is pinned first when visible and handleDragEnd refuses to move it or drop onto it, so "top"
  // is the first rendered row that ISN'T Message. One definition shared by the handler and the disabled
  // state, so they can't disagree about where the ends are.
  const firstMovableRenderedId = renderedFieldIds.find(id => {
    const f = visibleFields.find(v => v.fieldConfigurationId === id)
    return isMessageColumnHidden || f?.columnCode !== 'Message'
  })
  const lastRenderedId = renderedFieldIds[renderedFieldIds.length - 1]

  /**
   * Jump a column to the first or last rendered position. Dragging is fine for a nudge but poor across
   * a scrolling list, and it's unreachable by keyboard — these two cover the jumps people actually
   * reach for. Deliberately no up/down: a nudge is what dragging is already good at.
   */
  const handleMoveFieldTo = (fieldId: string, position: 'top' | 'bottom') => {
    const field = allFields.find(f => f.fieldConfigurationId === fieldId)
    if (!isMessageColumnHidden && field?.columnCode === 'Message') return

    const oldIndex = stageFieldsOrder.indexOf(fieldId)
    const anchorId = position === 'top' ? firstMovableRenderedId : lastRenderedId
    if (oldIndex === -1 || !anchorId) return

    const newIndex = stageFieldsOrder.indexOf(anchorId)
    if (newIndex === -1 || newIndex === oldIndex) return

    setStageFieldsOrder(arrayMove(stageFieldsOrder, oldIndex, newIndex))
  }

  // Toggle field visibility
  const handleToggleFieldVisibility = (fieldId: string, isVisible: boolean) => {
    const field = allFields.find(f => f.fieldConfigurationId === fieldId)
    const fieldName = field ? getLocalizedField(field, 'columnName', language) : 'Unknown Field'
    
    if (isVisible) {
      // Remove from visible fields (move to hidden)
      setPendingVisibleFields(prev => prev.filter(id => id !== fieldId))
      setStageFieldsOrder(prev => prev.filter(id => id !== fieldId))
      // trackColumnAction('hide', fieldName)
    } else {
      // Add to visible fields (move from hidden)
      setPendingVisibleFields(prev => [...prev, fieldId])
      
      // V2: Special handling for Message column - always add to the top
      if (!isMessageColumnHidden && field?.columnCode === 'Message') {
        setStageFieldsOrder(prev => [fieldId, ...prev])
      } else {
        // V2: Regular columns - add after message if it exists, otherwise at the end
        setStageFieldsOrder(prev => {
          if (!isMessageColumnHidden) {
            const messageFieldId = allFields.find(f => f.columnCode === 'Message')?.fieldConfigurationId
            const messageIndex = messageFieldId ? prev.indexOf(messageFieldId) : -1
            if (messageIndex >= 0) {
              return [...prev.slice(0, messageIndex + 1), fieldId, ...prev.slice(messageIndex + 1)]
            }
          }
          return [...prev, fieldId]
        })
      }
      // trackColumnAction('show', fieldName)
    }
  }

  // Apply changes
  const handleApply = async (createMode: boolean) => {
    if (!stageConfig) return;

    setIsApplying(true);
    try {
      const orderedCurrentStageIds = orderFieldsWithFixedColumns(stageFieldsOrder);

      if (isFromManageViews) {
        // Manage Views branch — saveViewSettings (different endpoint, supports new views).
        // For an existing managed view, base the payload on the snapshot we fetched
        // on open so we don't clobber other stages/filters with the live grid state.
        // For a new view (no viewId, no managedViewData) we still build from `configuration`.
        const stageConfigurations = managedViewData?.stageConfigurations
          ? managedViewData.stageConfigurations.map((stage: any) => {
              const stageConfigurationId = stage.stageConfigurationId || stage.StageConfigurationId
              const existingFieldIds: string[] = stage.fieldConfigurationIds || stage.FieldConfigurationIds || []
              if (stageConfigurationId === stageConfig.stageId) {
                return { stageConfigurationId, fieldConfigurationIds: orderedCurrentStageIds }
              }
              return { stageConfigurationId, fieldConfigurationIds: existingFieldIds }
            })
          : (configuration?.stageConfigurations.map(stage => {
              if (stage.stageCode.toLowerCase() === currentStage?.toLowerCase()) {
                return { stageConfigurationId: stage.stageId, fieldConfigurationIds: orderedCurrentStageIds }
              }
              return {
                stageConfigurationId: stage.stageId,
                fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
              }
            }) || []);

        // Live-grid column widths, JSON-stringified — used for new views and as a
        // defensive fallback when the managed view's snapshot is missing the field.
        const liveColumnWidths = state.columnWidths && Object.keys(state.columnWidths).length > 0
          ? JSON.stringify(state.columnWidths)
          : JSON.stringify({});

        // Existing managed view → preserve its own filters / column widths /
        // items-per-page from the snapshot so saving doesn't silently overwrite
        // them with whatever the user has open in the live grid right now.
        // New view (no viewId) → start from live state.
        const persistedStageFilters = managedViewData?.stageFilters ?? managedViewData?.StageFilters
        const persistedGlobalFilters = managedViewData?.globalFilters ?? managedViewData?.GlobalFilters
        const persistedColumnWidths = managedViewData?.columnWidths ?? managedViewData?.ColumnWidths
        const persistedItemsPerPage = managedViewData?.itemsPerPage ?? managedViewData?.ItemsPerPage

        const request = {
          id: viewId || undefined,
          title: viewTitle,
          isPublic,
          isVisible: true,
          isDefault: undefined,
          stageConfigurations,
          stageFilters: !viewId
            ? []
            : (persistedStageFilters ?? serializeStageFilters(stageFilters)),
          globalFilters: !viewId
            ? []
            : (persistedGlobalFilters ?? serializeGlobalFilters(globalFilters)),
          visibleViewIds: buildVisibleViewIds(viewsResponse),
          columnWidths: viewId
            ? (persistedColumnWidths ?? liveColumnWidths)
            : liveColumnWidths,
          itemsPerPage: viewId
            ? (persistedItemsPerPage ?? state.itemsPerPage)
            : state.itemsPerPage,
          isUserViewStateUpdate: false,
          updateFilters: createMode,
        };

        await saveViewSettings(request);
        try { await refreshViews(); } catch (e) { console.warn('Failed to refresh views:', e); }
        await refreshConfiguration(true, viewId || null);
      } else {
        // Personal-view branch — shared hook handles request shape, save, optimistic
        // updateVisibleColumns, and refreshConfiguration. Same flow used by the
        // Jump-to-Column "add hidden column" path.
        // The personal view IS the live config, so building from `configuration` is correct here.
        const stageConfigurations = configuration?.stageConfigurations.map(stage => {
          if (stage.stageCode.toLowerCase() === currentStage?.toLowerCase()) {
            return { stageConfigurationId: stage.stageId, fieldConfigurationIds: orderedCurrentStageIds }
          }
          return {
            stageConfigurationId: stage.stageId,
            fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
          }
        }) || [];

        const ok = await saveCurrentViewState({
          stageConfigurations,
          optimisticOrderedFieldIds: stageFieldsOrder,
        });
        if (!ok) return; // hook already toasted; finally still releases setIsApplying
      }

      // Track the view save action
      const actionType = isFromManageViews ? (viewId ? 'edit' : 'create') : 'save';
      const viewName = isFromManageViews ? viewTitle : (currentView?.title || 'Custom View');
      trackViewAction(actionType, viewName);

      onClose();
      toast({
        variant: "success",
        title: t('success'),
        description: isFromManageViews ? t('viewSavedSuccessfully') : t('columnConfigurationUpdatedSuccessfully'),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error'),
        description: error instanceof Error ? error.message : t('failedToSaveView'),
      });
    } finally {
      setIsApplying(false);
    }
  }

  // Cancel changes
  const handleCancel = () => {
    // Reset to original state
    if (stageConfig) {
      setPendingVisibleFields([...stageFieldIds])
      setStageFieldsOrder(stageConfig.fieldConfigurations.map(f => f.fieldConfigurationId))
    }
    onClose();
  }

  // Delete logic
  const handleDeleteClick = (fieldConfigId: string) => {
    setColumnToDelete(fieldConfigId)
  }
  
  const confirmDelete = async () => {
    if (columnToDelete) {
      setDeleting(true)
      try {
        const field = allFields.find(f => f.fieldConfigurationId === columnToDelete)
        const fieldName = field ? getLocalizedField(field, 'columnName', language) : 'Unknown Field'
        
        await deleteFieldConfiguration(columnToDelete)
        await refreshConfiguration(true)
        deleteColumn(columnToDelete)
        
        // Track the column deletion
        trackColumnAction('delete', fieldName)
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to delete column",
        })
      }
      setDeleting(false)
      setColumnToDelete(null)
    }
  }

  const isCreateMode = mode === 'create';
  const modalTitle = isCreateMode ? t('createView') : t('editColumns');
  const modalSubtitle = isCreateMode ? t('editColumnsSubtitle') : t('editColumnsSubtitle');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:w-[90vw] max-w-[900px] min-w-[320px] sm:min-w-[600px] h-[90vh] sm:h-[85vh] max-h-[800px] min-h-[400px] sm:min-h-[500px] flex flex-col resize overflow-auto" aria-describedby="edit-columns-description">
          <div id="edit-columns-description" className="sr-only">
            {modalTitle} - {t('manageColumnsDescription', { stage: currentStage || '' })}
          </div>
          <DialogHeader className="flex-shrink-0">
            <div className="text-left">
              <DialogTitle>{modalTitle}</DialogTitle>
              {(isCreateMode || isFromManageViews) && (
                <div className="mt-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('title')}
                    </label>
                    <Input
                      placeholder={t('nameYourCustomView')}
                      value={viewTitle}
                      onChange={(e) => onViewTitleChange?.(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('visibility')}
                    </label>
                    <div className="flex items-center space-x-3">
                      <Switch
                        checked={isPublic}
                        onCheckedChange={(checked) => onIsPublicChange?.(checked)}
                      />
                      <span className="text-sm text-gray-600">
                        {isPublic ? t('public') : t('private')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-600 mt-2">{modalSubtitle}</p>
            </div>
          </DialogHeader>
          
          {/* Unified search bar */}
          <div className="flex-shrink-0 mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={16} />
              <Input
                placeholder={t('searchAllColumns') || 'Search all columns...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-8 bg-gray-50 border border-gray-300 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors"
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label={t('clearSearch') || 'Clear search'}
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer z-10"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 min-h-0">
            {/* Left Column - Hidden Fields */}
            <div className="flex flex-col min-h-0">
              <div className="mb-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <EyeOff size={14} className="text-gray-500" />
                  <h3 className="text-sm font-medium text-gray-900">{t('hiddenColumns')}</h3>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border rounded-md max-h-[200px] lg:max-h-none">
                {/* h-12 matches the visible pane, which needs the height for its stacked reorder buttons
                    — the two lists sit side by side and rows at different heights read as a mistake.
                    Fixed height rather than padding on both sides, so neither drifts when its content
                    changes. Keep them in step. */}
                {nonStageFields.map((field) => (
                  <div key={field.fieldConfigurationId} className="flex h-12 items-center justify-between px-3 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span 
                        className="truncate text-sm flex items-center gap-1"
                        style={{ color: field.mappingSource ? '#9333ea' : (field.isCustomField ? '#2563eb' : undefined) }}
                      >
                        {field.isPrivate && (
                          <Lock size={12} className="text-gray-500 flex-shrink-0" />
                        )}
                        {getLocalizedField(field, 'columnName', language)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ColumnActions
                        column={field}
                        onEdit={() => { setEditingColumn(field); setIsEditFieldModalOpen(true); }}
                        onDelete={() => handleDeleteClick(field.fieldConfigurationId)}
                        canDeleteCustomColumns={canDeleteCustomColumns}
                        canEditCustomColumns={canEditCustomColumns}
                        canEditDefaultSettings={canEditDefaultSettings}
                        isVisible={false}
                        onToggleVisibility={() => handleToggleFieldVisibility(field.fieldConfigurationId, false)}
                      />
                    </div>
                  </div>
                ))}
                {nonStageFields.length === 0 && (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    No hidden columns
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Visible Fields */}
            <div className="flex flex-col min-h-0">
              <div className="mb-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Eye size={14} className="text-gray-500" />
                  <h3 className="text-sm font-medium text-gray-900">{t('visibleColumns')}</h3>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border rounded-md max-h-[200px] lg:max-h-none">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={stageFieldsOrder} strategy={verticalListSortingStrategy}>
                    {stageFieldsOrder.map((id) => {
                      const field = visibleFields.find(f => f.fieldConfigurationId === id)
                      if (!field) return null
                      const isFixedColumn = !isMessageColumnHidden && field.columnCode === 'Message' // V2: Only Message is fixed
                      return (
                        <SortableItem key={field.fieldConfigurationId} id={field.fieldConfigurationId} disabled={isFixedColumn}>
                          {/* h-12 fixed, NOT vertical padding: the reorder buttons make this row's content
                              taller than the hidden pane's, so padding-driven heights drifted apart every
                              time either side changed. Both panes now state the same height outright.
                              The last-row border is suppressed by comparing against `lastRenderedId`, NOT
                              with `last:border-b-0` as the hidden pane does: each row here is wrapped in
                              its own <SortableItem>, so every row is its parent's last child and the CSS
                              modifier stripped the divider from ALL of them. Suppressing it matters —
                              otherwise the final row shows a divider with nothing under it, and doubles
                              against the container border when the list fills the box. */}
                          <div className={`flex h-12 items-center justify-between px-3 ${field.fieldConfigurationId === lastRenderedId ? '' : 'border-b border-gray-200'}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {isFixedColumn ? (
                                <div className="w-3.5 h-3.5" /> // Empty space to align with other items
                              ) : (
                                <GripVertical size={14} className="text-gray-400" />
                              )}
                              {/* Jump-to-end controls: after the grip, since dragging is the primary
                                  gesture and should stay leftmost. Stacked so the pair costs one icon's
                                  width rather than two, and each disables at its own end so a button never
                                  looks live while doing nothing.
                                  Sizing is deliberate. The two targets are adjacent and point OPPOSITE
                                  ways, so a mis-click doesn't no-op — it reorders the wrong way, and the
                                  row then has to be found again at the far end of a scrolling list. Each
                                  button takes a full-width 18px band with a hover background, so there's a
                                  visible boundary between them rather than two bare glyphs sharing an
                                  edge. Still short of WCAG 2.5.8's 24px, which this column can't afford —
                                  if that becomes a requirement, one menu button is the way to get it.
                                  Fixed h-10 on the wrapper — two 18px bands plus the 2px gap, with slack —
                                  so the Message row (which gets no buttons) keeps the same height as the
                                  rest instead of collapsing to 0. Must not be tighter than the content:
                                  h-9 was 2px short, and because the bands are column flex items with the
                                  default flex-shrink they silently rendered at 17px, handing back part of
                                  the target-size fix this is here to make. */}
                              <div className="flex h-10 w-4 flex-shrink-0 flex-col items-stretch justify-center gap-0.5">
                                {!isFixedColumn && (
                                  <>
                                    <button
                                      type="button"
                                      title={t('moveToTop')}
                                      aria-label={t('moveToTop')}
                                      disabled={field.fieldConfigurationId === firstMovableRenderedId}
                                      onClick={() => handleMoveFieldTo(field.fieldConfigurationId, 'top')}
                                      className="flex h-[18px] w-full items-center justify-center rounded-sm text-gray-400 hover:bg-gray-200 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-25"
                                    >
                                      <ArrowUpToLine size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      title={t('moveToBottom')}
                                      aria-label={t('moveToBottom')}
                                      disabled={field.fieldConfigurationId === lastRenderedId}
                                      onClick={() => handleMoveFieldTo(field.fieldConfigurationId, 'bottom')}
                                      className="flex h-[18px] w-full items-center justify-center rounded-sm text-gray-400 hover:bg-gray-200 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-25"
                                    >
                                      <ArrowDownToLine size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                              <span 
                                className="truncate text-sm flex items-center gap-1"
                                style={{ color: field.mappingSource ? '#9333ea' : (field.isCustomField ? '#2563eb' : undefined) }}
                              >
                                {field.isPrivate && (
                                  <Lock size={12} className="text-gray-500 flex-shrink-0" />
                                )}
                                {getLocalizedField(field, 'columnName', language)}
                                {isFixedColumn && <span className="text-xs text-gray-500 ml-2">({t('fixedPosition')})</span>}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <ColumnActions
                                column={field}
                                onEdit={() => { setEditingColumn(field); setIsEditFieldModalOpen(true); }}
                                onDelete={() => handleDeleteClick(field.fieldConfigurationId)}
                                canDeleteCustomColumns={canDeleteCustomColumns}
                                canEditCustomColumns={canEditCustomColumns}
                                canEditDefaultSettings={canEditDefaultSettings}
                                isVisible={true}
                                onToggleVisibility={canHideColumnFromView ? () => handleToggleFieldVisibility(field.fieldConfigurationId, true) : undefined}
                              />
                            </div>
                          </div>
                        </SortableItem>
                      )
                    })}
                  </SortableContext>
                </DndContext>
                {visibleFields.length === 0 && (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    No visible columns
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer with buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mt-4 pt-4 border-t flex-shrink-0">
            {!isCreateMode && !isFromManageViews && canEditCustomColumns && (
              <Button
                variant="ghost"
                className="text-blue-600 flex items-center gap-1 w-full sm:w-auto"
                onClick={() => setIsAddCustomColumnOpen(true)}
              >
                <Plus size={16} />
                {t('customColumn')}
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
              <Button variant="elliptical-outline" onClick={handleCancel} disabled={isApplying} className="flex-1 sm:flex-none">
                {t('cancel')}
              </Button>
              <Button variant="elliptical" onClick={() => handleApply(isCreateMode)} disabled={isApplying} className="flex-1 sm:flex-none">
                {isApplying ? t('processing') : (isCreateMode ? t('saveView') : t('save'))}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {isAddCustomColumnOpen && (
        <EditFieldModal
          isOpen={isAddCustomColumnOpen}
          onClose={() => setIsAddCustomColumnOpen(false)}
          addMode={true}
          canEditCustomColumns={canEditCustomColumns}
        />
      )}
      
      {columnToDelete && (
        <AlertDialog open={!!columnToDelete} onOpenChange={() => setColumnToDelete(null)}>
          <AlertDialogContent aria-describedby="delete-column-description">
            <div id="delete-column-description" className="sr-only">
              Confirmation dialog to delete a column permanently
            </div>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
              <AlertDialogDescription>This action will delete the column and cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel variant="elliptical-outline">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} variant="elliptical" disabled={deleting}>
                {deleting ? t('processing') : t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      
      {isEditFieldModalOpen && editingColumn && (
        <EditFieldModal
          isOpen={isEditFieldModalOpen}
          onClose={() => { setIsEditFieldModalOpen(false); setEditingColumn(null); }}
          fieldConfiguration={editingColumn}
          canEditCustomColumns={canEditCustomColumns && (
            // Mirror the per-row gate from <ColumnActions>: Default-partition fields
            // require EditDefaultSettings even after EditColumns is granted. The parent
            // gate at <ColumnActions> already prevents the pencil from rendering, but
            // gating here too means a future caller that opens the modal directly
            // (deep link, programmatic open) can't bypass it.
            !(typeof editingColumn?.tenantName === 'string' &&
              editingColumn.tenantName.toLowerCase() === 'default') ||
            canEditDefaultSettings
          )}
        />
      )}
    </>
  )
}
